import { logSystem } from 'logger';
import { LZString } from '@holmescorp/lz-string';

const iv = Uint8Array.from([89, 45, 124, 33, 151, 156, 38, 175, 126, 209, 136, 234, 238, 193, 84, 108]);
const baseSalt = Uint8Array.from([104, 143, 61, 144, 177, 155, 49, 124, 78, 183, 73, 90, 52, 31, 159, 79]);

const getKeyMaterial = async () => {
	const password = `${window.hcstatic.association}-product0data-statecache`;
	return await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, [
		'deriveBits',
		'deriveKey'
	]);
};

const getKey = async (keyMaterial: CryptoKey, salt: Uint8Array) => {
	return crypto.subtle.deriveKey(
		{
			name: 'PBKDF2',
			salt: salt,
			iterations: 100000,
			hash: 'SHA-256'
		},
		keyMaterial,
		{ name: 'AES-CBC', length: 256 },
		true,
		['encrypt', 'decrypt']
	);
};

const encryptionKey = async () => {
	const keyMaterial = await getKeyMaterial();
	return await getKey(keyMaterial, baseSalt);
};

const ckeypromise = encryptionKey();

const chunk = <T>(arr: T[], size: number): T[][] =>
	Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));

export const encrypt_item = async (item: Record<string, any>): Promise<string> => {
	try {
		const ckey = await ckeypromise;
		const encoded = new TextEncoder().encode(JSON.stringify(item));
		const d8 = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, ckey, encoded));

		// get around dynamically exceeding function argument count limitation
		const data = chunk(Array.from(d8), 16)
			.map((c) => String.fromCharCode(...c))
			.join('');
		return LZString.compressToUTF16(data);
	} catch (err) {
		logSystem.info(() => `error encrypting: ${err.name} ${err.message}`);
		return '';
	}
};

export async function decrypt_item<T>(item: string): Promise<{ [key: string]: T }> {
	try {
		const ckey = await ckeypromise;
		const decompressed = LZString.decompressFromUTF16(item);
		if (decompressed === null) {
			return {} as { [key: string]: T };
		}
		const encrypted = Uint8Array.from(Array.from(decompressed).map((v) => v.charCodeAt(0)));
		const decrypted = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, ckey, encrypted));
		const decoded = new TextDecoder().decode(decrypted);
		return JSON.parse(decoded) as { [key: string]: T };
	} catch (err) {
		logSystem.info(() => `error decrypting: ${err.name} ${err.message}`);
		return {} as { [key: string]: T };
	}
}
